// Dettaglio rapportino con materiali, firma e info principali
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, ExternalLink, FileText } from 'lucide-react'
import type { Report } from '@fieldcore/types'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import { Skeleton } from '../../components/ui/Skeleton'
import { formatDate, formatCurrency } from '../../lib/utils'
import api from '../../lib/api'

const STATUS_LABELS: Record<string, string> = {
  draft: 'Bozza',
  completed: 'Completato',
  signed: 'Firmato',
  sent: 'Inviato',
}

export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>()

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['report', id],
    queryFn: async () => {
      const res = await api.get<Report>(`/reports/${id}`)
      return res.data
    },
    enabled: Boolean(id),
  })

  const { data: ticketAttachments = [] } = useQuery({
    queryKey: ['ticket-attachments', report?.ticketId],
    queryFn: async () => {
      if (!report?.ticketId) return []
      try {
        const res = await api.get<Array<{ name: string; mimeType: string; size: number; url: string }>>(`/tickets/${report.ticketId}/attachments`)
        return res.data ?? []
      } catch { return [] }
    },
    enabled: Boolean(report?.ticketId),
  })

  if (error) return <Alert variant="error">Impossibile caricare il rapportino.</Alert>
  if (isLoading) return (
    <div className="space-y-4">
      <Skeleton height={100} className="rounded-lg" />
      <Skeleton height={200} className="rounded-lg" />
    </div>
  )
  if (!report) return <Alert variant="error">Rapportino non trovato.</Alert>

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader
        title={`Rapportino ${report.reportNumber}`}
        breadcrumb={[{ label: 'Rapportini', href: '/reports' }, { label: report.reportNumber }]}
        actions={
          <div className="flex gap-2">
            {report.pdfUrl && (
              <Button
                variant="secondary"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={async () => {
                  try {
                    const res = await api.get<Blob>(`/reports/${report.id}/pdf/download`, { responseType: 'blob' })
                    const blobUrl = URL.createObjectURL(res.data)
                    const a = document.createElement('a')
                    a.href = blobUrl
                    a.download = `rapportino-${report.reportNumber ?? report.id}.pdf`
                    a.click()
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
                  } catch {
                    alert('Errore download PDF')
                  }
                }}
              >
                Scarica PDF
              </Button>
            )}
            <Button
              variant="secondary"
              leftIcon={<ExternalLink className="h-4 w-4" />}
              onClick={() => window.open(`/reports/${id}/preview`, '_blank')}
            >
              Anteprima
            </Button>
          </div>
        }
      />

      {/* Info principali */}
      <Card>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {[
            { label: 'Tipo', value: <Badge variant="default">{report.type}</Badge> },
            { label: 'Stato', value: <Badge variant={report.status === 'signed' ? 'success' : 'warning'}>{STATUS_LABELS[report.status]}</Badge> },
            { label: 'Data creazione', value: formatDate(report.createdAt) },
            { label: 'Ticket', value: report.ticketId ? <Link to={`/tickets/${report.ticketId}`} className="text-[var(--fc-primary)] hover:underline">#{report.ticketId.slice(0, 8)}</Link> : <span>—</span> },
            { label: 'Ore lavorate', value: report.totalHours ? `${report.totalHours}h` : 'N/D' },
            { label: 'Km percorsi', value: report.travelKm ? `${report.travelKm} km` : 'N/D' },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs text-[var(--fc-foreground-subtle)] mb-1">{label}</p>
              <div className="text-sm text-[var(--fc-foreground)]">{value}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Descrizione lavoro */}
      {report.workDescription && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-2">Descrizione lavoro</h3>
          <p className="text-sm text-[var(--fc-foreground-muted)] whitespace-pre-wrap">{report.workDescription}</p>
        </Card>
      )}

      {/* Materiali utilizzati */}
      {report.materialsUsed && report.materialsUsed.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">Materiali utilizzati</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--fc-border)] text-left text-xs text-[var(--fc-foreground-muted)]">
                  <th className="pb-2 pr-4">Materiale</th>
                  <th className="pb-2 pr-4">Codice</th>
                  <th className="pb-2 pr-4 text-right">Qtà</th>
                  <th className="pb-2 text-right">Importo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--fc-border)]">
                {report.materialsUsed.map((mat, idx) => (
                  <tr key={idx}>
                    <td className="py-2 pr-4 font-medium text-[var(--fc-foreground)]">{mat.materialName}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-[var(--fc-foreground-muted)]">{mat.materialCode}</td>
                    <td className="py-2 pr-4 text-right">{mat.quantity}</td>
                    <td className="py-2 text-right">{mat.unitCost ? formatCurrency(mat.quantity * mat.unitCost) : 'N/D'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Firma cliente */}
      {report.customerSignatureUrl && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">Firma cliente</h3>
          <img
            src={report.customerSignatureUrl}
            alt="Firma cliente"
            className="max-h-24 rounded border border-[var(--fc-border)] bg-white"
          />
          {report.customerSignerName && (
            <p className="mt-2 text-sm text-[var(--fc-foreground-muted)]">{report.customerSignerName}</p>
          )}
        </Card>
      )}

      {/* Allegati del ticket */}
      {ticketAttachments.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">
            Allegati del ticket ({ticketAttachments.length})
          </h3>
          <div className="space-y-2">
            {ticketAttachments.map((att, idx) => {
              const isImage = att.mimeType?.startsWith('image/')
              const isPdf = att.mimeType === 'application/pdf'
              return (
                <div key={idx} className="flex items-center gap-3 rounded border border-[var(--fc-border)] p-3">
                  {isImage ? (
                    <img
                      src={att.url}
                      alt={att.name}
                      className="h-12 w-12 rounded object-cover border border-[var(--fc-border)]"
                    />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-[var(--fc-surface-overlay)] border border-[var(--fc-border)]">
                      <FileText className="h-6 w-6 text-[var(--fc-foreground-muted)]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--fc-foreground)] truncate">{att.name}</p>
                    <p className="text-xs text-[var(--fc-foreground-muted)]">
                      {isPdf ? 'PDF' : isImage ? 'Immagine' : 'File'} · {att.size ? `${Math.round(att.size / 1024)} KB` : ''}
                    </p>
                  </div>
                  <a
                    href={att.url}
                    target="_blank"
                    rel="noreferrer"
                    download={att.name}
                    className="flex items-center gap-1 rounded border border-[var(--fc-border)] px-2 py-1 text-xs text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Scarica
                  </a>
                </div>
              )
            })}
          </div>
        </Card>
      )}
    </div>
  )
}
